/**
 * Screen Wake Lock manager for background audio playback.
 *
 * A PWA's audio element keeps playing when the screen dims/locks ONLY if the
 * OS treats the tab as an active media session. On its own a backgrounded PWA
 * can be throttled or suspended, stopping the audio — unacceptable for a Bible
 * audio app where the listener has set the phone down with the screen off.
 *
 * Two complementary mechanisms keep audio alive:
 *   1. Screen Wake Lock  — holds the screen on while audio is actively playing
 *      so the page is never put to sleep mid-chapter (request on play, release
 *      on pause/stop/end).
 *   2. Media Session      — wired in player.ts; tells the OS this is a media
 *      app so the lock-screen transport + background-audio allowance kick in.
 *
 * The Wake Lock API auto-releases the sentinel whenever the document is hidden
 * (tab switch, screen off via power button). We listen for `visibilitychange`
 * and re-acquire when the document returns visible AND audio is still playing.
 *
 * Feature-detected: older iOS / browsers without `navigator.wakeLock` simply
 * no-op; MediaSession then carries background audio on its own.
 */

type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
};

type WakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};

let sentinel: WakeLockSentinelLike | null = null;
/** True while the user intends audio to be playing — drives re-acquisition. */
let wantLock = false;
let listenerBound = false;

function supported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'wakeLock' in navigator &&
    typeof (navigator as WakeLockNavigator).wakeLock?.request === 'function'
  );
}

async function acquire(): Promise<void> {
  if (!supported() || sentinel !== null) return;
  try {
    const s = await (navigator as WakeLockNavigator).wakeLock!.request('screen');
    sentinel = s;
    // The OS releases the sentinel on its own when the doc is hidden; clear our
    // reference so visibilitychange knows to re-request a fresh one.
    s.addEventListener('release', () => {
      if (sentinel === s) sentinel = null;
    });
  } catch {
    // Request can reject (low battery, doc not visible, permission). Non-fatal —
    // MediaSession still carries background audio; we retry on next visibility.
    sentinel = null;
  }
}

async function drop(): Promise<void> {
  const s = sentinel;
  sentinel = null;
  if (!s || s.released) return;
  try {
    await s.release();
  } catch {
    // Already released by the OS — nothing to do.
  }
}

function onVisibilityChange(): void {
  if (typeof document === 'undefined') return;
  // Wake locks auto-release on hide; re-acquire on return if still wanted.
  if (document.visibilityState === 'visible' && wantLock && sentinel === null) {
    void acquire();
  }
}

function ensureListener(): void {
  if (listenerBound || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  listenerBound = true;
}

/** Acquire the screen wake lock. Call when audio playback STARTS. */
export function acquireWakeLock(): void {
  wantLock = true;
  ensureListener();
  void acquire();
}

/** Release the screen wake lock. Call on pause / stop / end. */
export function releaseWakeLock(): void {
  wantLock = false;
  void drop();
}

/** Whether the Screen Wake Lock API is available in this environment. */
export function isWakeLockSupported(): boolean {
  return supported();
}
